import React, { useMemo } from "react";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import { EntityTypesSupportedByEmailFeature } from "@mapmycustomers/shared/types/map/types";
import { Button } from "@mapmycustomers/ui";

import { getEntityTypeDisplayName } from "@app/util/ui";

const RECIPIENT_TYPES: EntityTypesSupportedByEmailFeature[] = [
  EntityType.COMPANY,
  EntityType.PERSON,
];

interface EmailRecipientTypeSelectorProps {
  disabled?: boolean;
  onChange?: (value: EntityTypesSupportedByEmailFeature) => void;
  value: EntityTypesSupportedByEmailFeature;
}

const EmailRecipientTypeSelector: React.FC<EmailRecipientTypeSelectorProps> = ({
  disabled = false,
  onChange,
  value,
}) => {
  const intl = useIntl();

  const items = useMemo(() => {
    return RECIPIENT_TYPES.map((recipientType) => ({
      key: recipientType,
      label: getEntityTypeDisplayName(intl, recipientType, { lowercase: false, plural: false }),
      onClick: () => onChange?.(recipientType),
    }));
  }, [intl, onChange]);

  const selectedItem = useMemo(() => {
    return items.find((item) => item.key === value)!;
  }, [value, items]);

  return (
    <Tooltip
      title={
        disabled
          ? intl.formatMessage({
              id: "email.recipientTypeSelector.disabledTooltip",
              defaultMessage: "This is disabled because you have selected this record type.",
              description: "Email recipient type selector disabled tooltip",
            })
          : undefined
      }
    >
      <span>
        <Dropdown
          disabled={disabled}
          menu={{
            items: items,
            selectedKeys: value ? [value] : [],
          }}
          trigger={disabled ? [] : ["click"]}
        >
          <Button
            disabled={disabled}
            rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
            type="control"
          >
            {selectedItem.label}
          </Button>
        </Dropdown>
      </span>
    </Tooltip>
  );
};

export default EmailRecipientTypeSelector;
